import * as React from 'react';

import {
  ScanOutputFormat,
  type ScanDocumentResponse,
} from '@rematter/frontend-lib-api-client/grpc/sidecar/v1/scanner_local_api_pb';
import { Badge, Button, Col, Row, Select } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';
import { LucideDownload } from 'lucide-react';

import { $remoteScannerDetail } from '../../../_state/scannerDetail.state';
import { useTranslations } from '../../../i18n/useTranslations';
import type { ScannerDetailSidecar } from '../../../types/sidecar';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useScannerLocalClient } from '~/shared/providers/libp2p/useScannerLocalClient';

type Props = {
  scannerId: string;
  sidecarId: string;
  sidecarDetail: ScannerDetailSidecar | null;
};

type ParsedAamvaFieldRow = {
  id: string;
  key: string;
  value: string;
  source: 'pdf417' | 'ocr';
};

export const ScannerTabTest: React.FC<Props> = ({
  scannerId,
  sidecarId,
  sidecarDetail,
}) => {
  const t = useTranslations();
  const { refetch: refetchScannerDetail } = useAtomValue($remoteScannerDetail);
  const scannerClient = useScannerLocalClient(sidecarId);
  const { connection } = useHardwareSidecarConnection({
    sidecar: sidecarDetail,
    onConnectionFailed: refetchScannerDetail,
  });

  const [scanFormat, setScanFormat] = React.useState<ScanOutputFormat>(
    ScanOutputFormat.AAMVA,
  );
  const [timeoutMs, setTimeoutMs] = React.useState('30000');
  const [isScanning, setIsScanning] = React.useState(false);
  const [scanError, setScanError] = React.useState<string | null>(null);
  const [scanResult, setScanResult] =
    React.useState<ScanDocumentResponse | null>(null);

  const handleScan = React.useCallback(async () => {
    if (!scannerClient || !scannerId) return;
    setIsScanning(true);
    setScanError(null);

    try {
      const timeout = Number(timeoutMs);
      const response = await scannerClient.scanDocument(
        {
          scannerId,
          format: scanFormat,
          timeoutMs: timeout,
          includeImages: true,
        },
        {
          timeoutMs: timeout + 90_000,
        },
      );
      setScanResult(response);
    } catch (error) {
      setScanError(
        error instanceof Error
          ? error.message
          : t.hardwareView.scannerDetailView.messages.unknownScanError,
      );
    } finally {
      setIsScanning(false);
    }
  }, [scannerClient, scannerId, scanFormat, timeoutMs, t]);

  const handleDownloadImage = React.useCallback(
    (image: ScanDocumentResponse['images'][number]) => {
      const normalized = new Uint8Array(image.data.byteLength);
      normalized.set(image.data);
      const blob = new Blob([normalized], {
        type: image.contentType || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${scannerId}-${image.label}.bin`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    [scannerId],
  );

  const parsedAamvaFields = React.useMemo(() => {
    if (scanResult?.result.case !== 'aamva') {
      return [] as ParsedAamvaFieldRow[];
    }

    const rows: ParsedAamvaFieldRow[] = [];

    scanResult.result.value.fields.forEach((field, index) => {
      if (!field.key || !field.value) {
        return;
      }
      rows.push({
        id: `pdf417-${index}-${field.key}-${field.value}`,
        key: field.key,
        value: field.value,
        source: 'pdf417',
      });
    });

    scanResult.result.value.ocrFields.forEach((field, index) => {
      if (!field.key || !field.value) {
        return;
      }
      rows.push({
        id: `ocr-${index}-${field.key}-${field.value}`,
        key: field.key,
        value: field.value,
        source: 'ocr',
      });
    });

    rows.sort((a, b) => {
      if (a.key !== b.key) {
        return a.key.localeCompare(b.key);
      }
      if (a.source !== b.source) {
        return a.source.localeCompare(b.source);
      }
      return a.value.localeCompare(b.value);
    });

    return rows;
  }, [scanResult]);

  const imageUrls = React.useMemo(() => {
    if (!scanResult) return [];
    return scanResult.images.map((image) => {
      const normalized = new Uint8Array(image.data.byteLength);
      normalized.set(image.data);
      const blob = new Blob([normalized], {
        type: image.contentType || 'image/jpeg',
      });
      return URL.createObjectURL(blob);
    });
  }, [scanResult]);

  React.useEffect(
    () => () => {
      imageUrls.forEach((url) => URL.revokeObjectURL(url));
    },
    [imageUrls],
  );

  const formatOptions = React.useMemo(
    () => [
      {
        key: String(ScanOutputFormat.AAMVA),
        label: t.hardwareView.scannerDetailView.formats.aamva,
      },
      {
        key: String(ScanOutputFormat.RAW),
        label: t.hardwareView.scannerDetailView.formats.raw,
      },
    ],
    [t],
  );

  const timeoutOptions = React.useMemo(
    () => [
      { key: '1000', label: '1s' },
      { key: '5000', label: '5s' },
      { key: '10000', label: '10s' },
      { key: '30000', label: '30s' },
      { key: '60000', label: '60s' },
    ],
    [],
  );

  return (
    <div className="@ScannerTabTest flex max-h-full flex-col gap-4 p-4">
      <Row gap2 className="flex-wrap items-end">
        <Select
          value={String(scanFormat)}
          onChange={(key) => setScanFormat(Number(key) as ScanOutputFormat)}
          options={formatOptions}
          className="w-[140px]"
        />
        <Select
          value={timeoutMs}
          onChange={setTimeoutMs}
          options={timeoutOptions}
          className="w-[100px]"
        />
        <Button
          onPress={handleScan}
          isDisabled={!scannerClient || isScanning || !connection?.connected}
        >
          {isScanning
            ? t.hardwareView.scannerDetailView.actions.scanning
            : t.hardwareView.scannerDetailView.actions.scanNow}
        </Button>
      </Row>

      {scanError && (
        <div className="rounded bg-failure/10 p-2 text-sm text-failure">
          {scanError}
        </div>
      )}

      {scanResult && (
        <Col gap4>
          <Col>
            <h3 className="mb-2 text-base font-bold text-neutral">
              {t.hardwareView.scannerDetailView.sections.scanResult}
            </h3>
            <Row className="items-start gap-4 py-2">
              <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                {t.hardwareView.scannerDetailView.fields.elapsedMs}
              </div>
              <div className="text-sm">{scanResult.elapsedMs}</div>
            </Row>
          </Col>

          {scanResult.result.case === 'aamva' && (
            <Col gap4>
              <Col>
                <h3 className="mb-2 text-base font-bold text-neutral">
                  {t.hardwareView.scannerDetailView.sections.aamvaFields}
                </h3>
                <Col>
                  {scanResult.result.value.rawPayload && (
                    <Row className="items-start gap-4 py-2">
                      <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .rawPayload
                        }
                      </div>
                      <div className="text-sm break-all">
                        {scanResult.result.value.rawPayload}
                      </div>
                    </Row>
                  )}
                  {scanResult.result.value.pdf417Payload && (
                    <Row className="items-start gap-4 py-2">
                      <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .pdf417Payload
                        }
                      </div>
                      <div className="text-sm break-all">
                        {scanResult.result.value.pdf417Payload}
                      </div>
                    </Row>
                  )}
                  <Row className="items-start gap-4 py-2">
                    <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                      {
                        t.hardwareView.scannerDetailView.scanResultLabels
                          .pdf417Decoded
                      }
                    </div>
                    <div className="text-sm">
                      {scanResult.result.value.pdf417Decoded
                        ? t.hardwareView.scannerDetailView.scanResultLabels.yes
                        : t.hardwareView.scannerDetailView.scanResultLabels
                            .no}{' '}
                      (
                      {(scanResult.result.value.pdf417Confidence ?? 0).toFixed(
                        3,
                      )}
                      )
                    </div>
                  </Row>
                  {scanResult.result.value.frontImageLabel && (
                    <Row className="items-start gap-4 py-2">
                      <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .frontImageLabel
                        }
                      </div>
                      <div className="text-sm">
                        {scanResult.result.value.frontImageLabel}
                      </div>
                    </Row>
                  )}
                  {scanResult.result.value.backImageLabel && (
                    <Row className="items-start gap-4 py-2">
                      <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .backImageLabel
                        }
                      </div>
                      <div className="text-sm">
                        {scanResult.result.value.backImageLabel}
                      </div>
                    </Row>
                  )}
                  {scanResult.result.value.portraitImageLabel && (
                    <Row className="items-start gap-4 py-2">
                      <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .portraitImageLabel
                        }
                      </div>
                      <div className="text-sm">
                        {scanResult.result.value.portraitImageLabel}
                      </div>
                    </Row>
                  )}
                </Col>
              </Col>

              <Col>
                <h3 className="mb-2 text-base font-bold text-neutral">
                  {t.hardwareView.scannerDetailView.scanResultLabels.parsedFields(
                    parsedAamvaFields.length,
                  )}
                </h3>
                {parsedAamvaFields.length === 0 && (
                  <div className="text-sm text-neutral/70">
                    {
                      t.hardwareView.scannerDetailView.scanResultLabels
                        .noParsedFields
                    }
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-8">
                  {parsedAamvaFields.map((field) => (
                    <Row
                      key={field.id}
                      className="items-baseline gap-2 py-2 text-sm"
                    >
                      <span className="shrink-0 font-mono text-neutral/60">
                        {field.key}
                      </span>
                      <Badge
                        color={
                          field.source === 'pdf417' ? 'primary' : 'neutral'
                        }
                      >
                        {
                          t.hardwareView.scannerDetailView.scanResultLabels
                            .sourceLabels[field.source]
                        }
                      </Badge>
                      <span className="break-all">{field.value}</span>
                    </Row>
                  ))}
                </div>
              </Col>

              {scanResult.result.value.ocrText && (
                <Col>
                  <h3 className="mb-2 text-base font-bold text-neutral">
                    {t.hardwareView.scannerDetailView.scanResultLabels.ocrText}
                  </h3>
                  <pre className="max-h-40 overflow-auto rounded border border-neutral/10 p-2 text-xs whitespace-pre-wrap">
                    {scanResult.result.value.ocrText}
                  </pre>
                </Col>
              )}

              {Object.keys(scanResult.result.value.diagnostics ?? {}).length >
                0 && (
                <Col>
                  <h3 className="mb-2 text-base font-bold text-neutral">
                    {
                      t.hardwareView.scannerDetailView.scanResultLabels
                        .diagnostics
                    }
                  </h3>
                  <Col>
                    {Object.entries(scanResult.result.value.diagnostics).map(
                      ([key, value]) => (
                        <Row key={key} className="items-start gap-4 py-2">
                          <div className="w-[180px] shrink-0 text-sm text-neutral/60">
                            <span className="font-mono">{key}</span>
                          </div>
                          <div className="text-sm">{value}</div>
                        </Row>
                      ),
                    )}
                  </Col>
                </Col>
              )}
            </Col>
          )}

          {scanResult.images.length > 0 && (
            <Col gap2>
              <h3 className="text-base font-bold text-neutral">
                {t.hardwareView.scannerDetailView.sections.images}
              </h3>
              <div
                className={
                  scanResult.images.length > 1
                    ? 'grid grid-cols-2 gap-2'
                    : 'flex flex-col gap-2'
                }
              >
                {scanResult.images.map((image, index) => (
                  <Col
                    key={`${image.label}-${index}`}
                    gap2
                    className="rounded-lg border border-neutral/20 p-2"
                  >
                    <div className="text-xs font-medium text-neutral/50">
                      {image.label}
                    </div>
                    <img
                      src={imageUrls[index]}
                      alt={image.label}
                      className="max-h-64 rounded object-contain"
                    />
                    <Row gap2>
                      <Button
                        variant="outline"
                        size="default"
                        onPress={() => handleDownloadImage(image)}
                      >
                        <LucideDownload className="size-4" />
                        {t.hardwareView.scannerDetailView.actions.download}
                      </Button>
                    </Row>
                  </Col>
                ))}
              </div>
            </Col>
          )}
        </Col>
      )}
    </div>
  );
};
